/**
 * Hook: useCreatePipeline
 *
 * Cria uma nova pipeline no tenant atual
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { ROLES } from '@/features/auth/types';
import { Pipeline, PipelinePerfilUsuario, PipelineType } from '../types';

interface CreatePipelineInput {
  nome: string;
  tipo?: PipelineType;
  perfilUsuario?: PipelinePerfilUsuario | PipelinePerfilUsuario[];
}

export function useCreatePipeline() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePipelineInput) => {
      if (!profile?.tenant_id || !profile?.uuid) {
        throw new Error('Usuário ou tenant não encontrado');
      }

      const perfilUsuario = input.perfilUsuario ?? mapRoleToPerfilUsuario(profile.role);
      const perfilUsuarioArray = Array.isArray(perfilUsuario) ? perfilUsuario : [perfilUsuario];

      const { data, error } = await supabase
        .from('pipelines')
        .insert({
          tenant_id: profile.tenant_id,
          nome: input.nome.trim(),
          tipo: input.tipo ?? 'qualificacao',
          perfil_usuario: perfilUsuarioArray,
          tenant_ids: [profile.tenant_id],
          permitir_associados: false,
          criada_por: profile.uuid,
          ativa: true
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Erro ao criar pipeline: ${error.message}`);
      }

      return data as Pipeline;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelinesList'] });
    }
  });
}

function mapRoleToPerfilUsuario(userRole?: string): PipelinePerfilUsuario {
  const roleMap: Record<string, PipelinePerfilUsuario> = {
    [ROLES.RELACIONADOR]: 'relacionador',
    [ROLES.LIDER_VENDAS]: 'lider_vendas',
    [ROLES.SUPERVISOR]: 'supervisor',
    [ROLES.POS_VENDA]: 'especialista_pos_venda',
    [ROLES.SUPER_ADMIN]: 'admin',
    [ROLES.ASSOCIADO]: 'admin',
    [ROLES.SECRETARIA]: 'admin',
  };

  return roleMap[userRole || ''] || 'relacionador';
}
